import DeliveryMethodType, { DeliveryMethodAttribute } from 'common/constants/product/deliveryMethodType'
import { OptionWrapperType } from 'common/constants/product/optionWrapperType'
import { Product } from 'common/types/entities/api/product'
import { SelectedProductOption } from 'common/types/naverPay'
import DeliveryFeePayMethodType from 'common/constants/product/deliveryFeePayMethodType'
import { getDiscountPrice } from '../../types/entities/mongo/product'
import {
  ColorOption,
  CombinationOption,
  SimpleOption,
  SizeOption,
  SupplementProductOption,
  TextOption,
} from '../../types/entities/api/product'
import { StateKey } from 'common/utils/flipState'
import { isPcEnv } from '../../utils/common'
import { SSChannel } from '../../types/entities/api/channel'
export interface SelectedDeliveryOption {
  attribute: DeliveryMethodAttribute
  feePayMethodType: DeliveryFeePayMethodType
  price?: number
}

export interface SelectedOptions {
  product: { [key: string]: SelectedProductOption[] }
  coordi: { [key: string]: SelectedProductOption[] }
  delivery: { [key: string]: SelectedDeliveryOption }
  textOptions?: TextOption[]
  simpleOptions?: SimpleOption[]
  combinationOptions?: CombinationOption[]
  supplementProducts?: SupplementProductOption[]
  standardOptions?: (ColorOption | SizeOption)[]
  colorOptions?: ColorOption
  sizeOptions?: SizeOption
}

const initialOption: SelectedOptions = {
  product: {},
  coordi: {},
  delivery: {},
  textOptions: null,
  simpleOptions: null,
  combinationOptions: null,
  standardOptions: null,
  supplementProducts: null,
  colorOptions: null,
  sizeOptions: null,
}

const INIT_PRODUCT = 'option/INIT_PRODUCT'
const INIT_COORDI = 'option/INIT_COORDI'
const INIT_SELECTEDOPTION = 'option/INIT_SELECTEDOPTION'
const SET_PRODUCT = 'option/SET_PRODUCT'
const SET_COORDI = 'option/SET_COORDI'
const SET_DELIVERY = 'delivery/SET_DELIVERY'
const SET_TEXT_OPTIONS = 'option/SET_TEXT_OPTIONS'
const SET_SIMPLE_OPTIONS = 'option/SET_SIMPLE_OPTIONS'
const SET_COMBINATION_OPTIONS = 'option/SET_COMBINATION_OPTIONS'
const SET_SUPPLEMENT_PRODUCTS = 'option/SET_SUPPLEMENT_PRODUCTS'
const SET_STANDARD_OPTIONS = 'option/SET_STANDARD_OPTIONS'
const SET_SELECTED_STANDARD_PRODUCTS = 'option/SET_SELECTED_STANDARD_PRODUCTS'
const SET_SELECTED_STANDARD_COORDI = 'option/SET_SELECTED_STANDARD_COORDI'
const INIT_STANDARD_OPTION = 'option/INIT_STANDARD_OPTION'

const getInitOption = (product: Product, channel?: SSChannel): SelectedProductOption[] => {
  const { benefitsView, purchaseQuantityInfo, category, ssChannel } = product
  const minPurchaseCount = (purchaseQuantityInfo && purchaseQuantityInfo.minPurchaseQuantity) || 1
  const discountPrice = getDiscountPrice(product)

  return product.optionUsable
    ? []
    : [
        {
          id: product.id,
          isSupplement: false,
          count: minPurchaseCount,
          price: discountPrice,
          extraCharge: 0,
          totalPrice: discountPrice * minPurchaseCount,
          showRemoveButton: false,
          optionTitle: [product.windowProduct.name],
          sellerDiscountPrice: isPcEnv
            ? benefitsView.sellerImmediateDiscountAmount
            : benefitsView.mobileSellerImmediateDiscountAmount,
          managerDiscountPrice: isPcEnv
            ? benefitsView.managerImmediateDiscountAmount
            : benefitsView.mobileManagerImmediateDiscountAmount,
          naverPaySellerNo: product.channel.naverPaySellerNo,
          originPrice: product.salePrice,
          productName: product.windowProduct.name,
          itemName: null,
          largeCategorizeCategoryId: category && category.category1Id,
          middleCategorizeCategoryId: category && category.category2Id,
          smallCategorizeCategoryId: category && category.category3Id,
          detailCategorizeCategoryId: category && category.category4Id,
          largeCategorizeCategoryName: category && category.category1Name,
          middleCategorizeCategoryName: category && category.category2Name,
          smallCategorizeCategoryName: category && category.category3Name,
          detailCategorizeCategoryName: category && category.category4Name,
          naSiteId: ssChannel ? ssChannel.naSiteId : channel.naSiteId,
        },
      ]
}

const getDeliveryInfo = (product: Product): SelectedDeliveryOption => {
  const { productDeliveryInfo } = product
  if (productDeliveryInfo) {
    return {
      price: productDeliveryInfo.baseFee,
      attribute: DeliveryMethodType.getTypes(productDeliveryInfo.deliveryMethodTypes)[0],
      feePayMethodType: productDeliveryInfo.deliveryFeePayType,
    }
  }
  return null
}

export const initializeOption = (curState: StateKey, product: Product) => async dispatch => {
  const options = {}
  const delivery = {}
  const colorOptions = {}
  const sizeOptions = {}
  const standardOptions = {}
  const { textOptions, simpleOptions, combinationOptions, supplements } = product
  options[product.id] = getInitOption(product)
  delivery[product.id] = getDeliveryInfo(product)
  standardOptions[product.id] = product.standardOptions
  colorOptions[product.id] =
    product.standardOptions && product.standardOptions.find(standardOption => standardOption.groupName === '색상')
  sizeOptions[product.id] =
    product.standardOptions && product.standardOptions.find(standardOption => standardOption.groupName === '사이즈')

  dispatch({
    curState,
    type: INIT_PRODUCT,
    payload: {
      options,
      delivery,
      textOptions,
      simpleOptions,
      combinationOptions,
      colorOptions,
      sizeOptions,
      standardOptions,
      supplementProducts: supplements,
    },
  })
}

export const initializeSelectedOption = (curState: StateKey, selectedOption: SelectedOptions) => {
  return {
    curState,
    selectedOption,
    type: INIT_SELECTEDOPTION,
  }
}

export const initializeCoordiOption = (curState: StateKey, coordiProducts: Product[]) => async dispatch => {
  const options = {}
  const delivery = {}
  const colorOptions = {}
  const sizeOptions = {}
  const standardOptions = {}

  coordiProducts.forEach(coordiProduct => {
    options[coordiProduct.id] = getInitOption(coordiProduct)
    delivery[coordiProduct.id] = getDeliveryInfo(coordiProduct)
    colorOptions[coordiProduct.id] =
      coordiProduct.standardOptions &&
      coordiProduct.standardOptions.find(standardOption => standardOption.groupName === '색상')
    sizeOptions[coordiProduct.id] =
      coordiProduct.standardOptions &&
      coordiProduct.standardOptions.find(standardOption => standardOption.groupName === '사이즈')
    standardOptions[coordiProduct.id] = coordiProduct.standardOptions
  })

  dispatch({
    curState,
    options,
    delivery,
    colorOptions,
    sizeOptions,
    standardOptions,
    type: INIT_COORDI,
  })
}

export const initializeCoordiOptionByProducts = (curState: StateKey, products: Product[], channel?: SSChannel) => {
  const optionsAndDelivery = products.reduce(
    (obj, product) => ({
      options: { ...obj.options, [product.id]: getInitOption(product, channel) },
      delivery: { ...obj.delivery, [product.id]: getDeliveryInfo(product) },
      colorOptions: {
        ...obj.colorOptions,
        [product.id]:
          product.standardOptions &&
          product.standardOptions.find(standardOption => standardOption.groupName === '색상'),
      },
      sizeOptions: {
        ...obj.sizeOptions,
        [product.id]:
          product.standardOptions &&
          product.standardOptions.find(standardOption => standardOption.groupName === '사이즈'),
      },
      standardOptions: { ...obj.standardOptions, [product.id]: product.standardOptions },
    }),
    { options: {}, delivery: {}, colorOptions: {}, sizeOptions: {}, standardOptions: {} },
  )

  return {
    curState,
    options: optionsAndDelivery.options,
    delivery: optionsAndDelivery.delivery,
    colorOptions: optionsAndDelivery.colorOptions,
    sizeOptions: optionsAndDelivery.sizeOptions,
    standardOptions: optionsAndDelivery.standardOptions,
    type: INIT_COORDI,
  }
}

export const updateSelectedCoordiOptions = (
  curState: StateKey,
  optionWrapperType: OptionWrapperType,
  productId: string,
  selectedOption: SelectedProductOption[],
) => {
  const options = {}
  options[productId] = selectedOption
  return {
    curState,
    type: optionWrapperType === OptionWrapperType.product ? SET_PRODUCT : SET_COORDI,
    payload: {
      options,
    },
  }
}

export const updateSelectedOptions = (
  curState: StateKey,
  optionWrapperType: OptionWrapperType,
  productId: string,
  selectedOption: SelectedProductOption[],
  textOptions: TextOption[],
  simpleOptions: SimpleOption[],
  combinationOptions: CombinationOption[],
  supplementProducts: SupplementProductOption[],
  colorOptions: ColorOption,
  sizeOptions: SizeOption,
) => {
  const options = {}
  options[productId] = selectedOption
  return {
    curState,
    type: optionWrapperType === OptionWrapperType.product ? SET_PRODUCT : SET_COORDI,
    payload: {
      options,
      textOptions,
      simpleOptions,
      combinationOptions,
      supplementProducts,
      colorOptions,
      sizeOptions,
    },
  }
}

export const setDeliveryOption = (curState: StateKey, productId: string, deliveryOption: SelectedDeliveryOption) => {
  const delivery = {}
  delivery[productId] = deliveryOption

  return {
    curState,
    type: SET_DELIVERY,
    payload: {
      delivery,
    },
  }
}

export const setTextOptions = (curState: StateKey, textOptions: TextOption[]) => {
  return {
    curState,
    textOptions,
    type: SET_TEXT_OPTIONS,
  }
}

export const setSimpleOptions = (curState: StateKey, simpleOptions: SimpleOption[]) => {
  return {
    curState,
    simpleOptions,
    type: SET_SIMPLE_OPTIONS,
  }
}

export const setCombinationOptions = (curState: StateKey, combinationOptions: CombinationOption[]) => {
  return {
    curState,
    combinationOptions,
    type: SET_COMBINATION_OPTIONS,
  }
}

export const setSupplementProducts = (curState: StateKey, supplementProducts: SupplementProductOption[]) => {
  return {
    curState,
    supplementProducts,
    type: SET_SUPPLEMENT_PRODUCTS,
  }
}

export const setStandardOptions = (curState: StateKey, productId: string, colorOptions: any, sizeOptions: any) => {
  const color = {}
  const size = {}
  color[productId] = colorOptions
  size[productId] = sizeOptions
  return {
    curState,
    colorOptions: color,
    sizeOptions: size,
    type: SET_STANDARD_OPTIONS,
  }
}

export const updatedStandardSelectedOptions = (
  curState: StateKey,
  optionWrapperType: OptionWrapperType,
  productId: string,
  selectedOption: SelectedProductOption[],
  colorOptions,
  sizeOptions,
) => {
  const options = {}
  const colorOpt = {}
  const sizeOpt = {}
  options[productId] = selectedOption
  colorOpt[productId] = colorOptions
  sizeOpt[productId] = sizeOptions
  const wrapperType =
    optionWrapperType === OptionWrapperType.product ? SET_SELECTED_STANDARD_PRODUCTS : SET_SELECTED_STANDARD_COORDI
  return {
    curState,
    options,
    colorOptions: colorOpt,
    sizeOptions: sizeOpt,
    type: wrapperType,
  }
}

export const initStandardOption = (curState: StateKey, productId: string, colorOptions, sizeOptions) => {
  return {
    curState,
    colorOptions,
    sizeOptions,
    type: INIT_STANDARD_OPTION,
  }
}

const optionReducer = (state = initialOption, action) => {
  switch (action.type) {
    case INIT_PRODUCT:
      return {
        ...state,
        product: action.payload.options,
        delivery: action.payload.delivery,
        textOptions: action.payload.textOptions,
        simpleOptions: action.payload.simpleOptions,
        combinationOptions: action.payload.combinationOptions,
        standardOptions: action.payload.standardOptions,
        supplementProducts: action.payload.supplementProducts,
        colorOptions: action.payload.colorOptions,
        sizeOptions: action.payload.sizeOptions,
      }
    case INIT_COORDI:
      return {
        ...state,
        coordi: action.options,
        delivery: { ...state.delivery, ...action.delivery },
        colorOptions: { ...state.colorOptions, ...action.colorOptions },
        sizeOptions: { ...state.sizeOptions, ...action.sizeOptions },
        standardOptions: { ...state.standardOptions, ...action.standardOptions },
      }
    case SET_PRODUCT:
      return {
        ...state,
        product: { ...state.product, ...action.payload.options },
        textOptions: action.payload.textOptions,
        simpleOptions: action.payload.simpleOptions,
        combinationOptions: action.payload.combinationOptions,
        supplementProducts: action.payload.supplementProducts,
        colorOptions: action.payload.colorOptions,
        sizeOptions: action.payload.sizeOptions,
      }
    case SET_COORDI:
      return {
        ...state,
        coordi: { ...state.coordi, ...action.payload.options },
        colorOptions: action.payload.colorOptions,
        sizeOptions: action.payload.sizeOptions,
      }
    case SET_DELIVERY:
      return {
        ...state,
        delivery: { ...state.delivery, ...action.payload.delivery },
      }
    case SET_TEXT_OPTIONS:
      return {
        ...state,
        textOptions: action.textOptions,
      }
    case SET_SIMPLE_OPTIONS:
      return {
        ...state,
        simpleOptions: action.simpleOptions,
      }
    case SET_COMBINATION_OPTIONS:
      return {
        ...state,
        combinationOptions: action.combinationOptions,
      }
    case SET_SUPPLEMENT_PRODUCTS:
      return {
        ...state,
        supplementProducts: action.supplementProducts,
      }
    case INIT_SELECTEDOPTION:
      return {
        ...action.selectedOption,
      }
    case SET_STANDARD_OPTIONS:
      return {
        ...state,
        colorOptions: { ...state.colorOptions, ...action.colorOptions },
        sizeOptions: { ...state.sizeOptions, ...action.sizeOptions },
        standardOptions: { ...state.standardOptions, ...action.standardOptions },
      }
    case SET_SELECTED_STANDARD_PRODUCTS:
      return {
        ...state,
        product: { ...state.product, ...action.options },
        colorOptions: { ...state.colorOptions, ...action.colorOptions },
        sizeOptions: { ...state.sizeOptions, ...action.sizeOptions },
        standardOptions: { ...state.standardOptions, ...action.standardOptions },
      }
    case SET_SELECTED_STANDARD_COORDI:
      return {
        ...state,
        coordi: { ...state.coordi, ...action.options },
        colorOptions: { ...state.colorOptions, ...action.colorOptions },
        sizeOptions: { ...state.sizeOptions, ...action.sizeOptions },
        standardOptions: { ...state.standardOptions, ...action.standardOptions },
      }
    case INIT_STANDARD_OPTION:
      return {
        ...state,
        colorOptions: { ...state.colorOptions, ...action.colorOptions },
        sizeOptions: { ...state.sizeOptions, ...action.sizeOptions },
      }
    default:
      return state
  }
}

export default optionReducer
